import { formatDistance, formatDuration, getLang, tr } from '@app/i18n'
import {
  floorOfLevel,
  isParking,
  localized,
  planRoute,
  productsOnShelf,
  resolveStart,
  shelfCodeOf,
  type Venue,
} from '@app/rvf'
import { useEffect, useMemo } from 'preact/hooks'
import { closeTarget, setTravelMode, showRoute } from '../actions'
import { rememberCar } from '../car-actions'
import { liveWebsite } from '../live-card'
import type { LiveOccupant } from '../occupants'
import { addToList, removeFromList, shoppingKeys } from '../shopping'
import {
  anchorId,
  cardDetailsOpen,
  externalLeg,
  geoStart,
  graph,
  langTick,
  liveOccupants,
  parkedCar,
  qrPassTargetId,
  routePlan,
  stepFreeOk,
  targetId,
  toggleCardDetails,
  travelMode,
  venue,
  venueMap,
} from '../state'
import { showTravelChoice } from '../travel-choice'
import { ClimbNoAltHint, ClimbPicker } from './climb-picker'
import { DoorBadges, doorFactsOf } from './door-badges'
import { GLYPH_PARKING, GLYPH_PHONE, GLYPH_QR, GLYPH_WHEELCHAIR } from './glyphs'
import { categoryLabel, featureCategory, featureName, planLevelsLabel } from './labels'
import { PhotoStrip } from './photo-strip'
import {
  BuildingDetails,
  EditInCabinetLink,
  hasCardDetails,
  TenantDetails,
} from './target-card-full'
import { TRAVEL_ICONS } from './travel-icons'

/** Живой слой (ADR 0014) поверх статики venue.json — по unit_id. */
/** Подпись этажа для карточки: « · 2 этаж». Пусто, если этаж один. */
function floorOfTarget(v: Venue, id: string): string {
  if (v.levels.length < 2) return ''
  const level = (v.units.find((u) => u.id === id) ?? v.pois.find((p) => p.id === id))?.level
  if (!level) return ''
  return ` · ${tr('volume3d.floor_n', { n: String(floorOfLevel(v, level)) })}`
}

function liveOf(id: string): LiveOccupant | null {
  return liveOccupants.value?.get(id) ?? null
}

function phoneOf(venueId: string): string | null {
  const v = venue.value
  if (!v) return null
  const live = liveOf(venueId)
  if (live?.phone) return live.phone
  const unit = v.units.find((u) => u.id === venueId)
  if (unit?.occupants?.[0]?.phone) return unit.occupants[0].phone
  const poi = v.pois.find((p) => p.id === venueId)
  const attrPhone = poi?.attrs?.['phone']
  return typeof attrPhone === 'string' ? attrPhone : null
}

function hoursOf(v: NonNullable<typeof venue.value>, id: string): string | null {
  const live = liveOf(id)
  if (live?.hours) return live.hours
  const unit = v.units.find((u) => u.id === id)
  const unitHours = unit?.attrs?.['hours']
  if (typeof unitHours === 'string') return unitHours
  const poi = v.pois.find((p) => p.id === id)
  const hours = poi?.attrs?.['hours']
  return typeof hours === 'string' ? hours : null
}

/**
 * Второе имя в подписи — то, которого НЕТ в заголовке.
 *
 * Заголовок берёт имя из живой карточки (live-card.ts). Тогда в подписи
 * полезно показать имя из карты: человек стоит перед дверью со старой вывеской
 * «Ristikheina Kohvik» и должен понять, что «Кафе Морого» — это она и есть.
 * Если карточки нет, заголовок пришёл из карты — и в подписи, как раньше,
 * уместен арендатор из статики.
 */
function secondaryNameOf(v: NonNullable<typeof venue.value>, id: string): string | null {
  const title = featureName(v, id)
  const live = liveOf(id)
  if (live?.name && localized(live.name, getLang(), v.venue.default_language)) {
    const unit = v.units.find((u) => u.id === id)
    const fromMap = localized(unit?.name, getLang(), v.venue.default_language)
    return fromMap && fromMap !== title ? fromMap : null
  }
  const occupant = v.units.find((u) => u.id === id)?.occupants?.[0]?.name ?? null
  return occupant && occupant !== title ? occupant : null
}

/** attrs любой цели: здание → точка → узел графа (двери/лестницы тоже цели). */
function staticAttrsOf(
  v: NonNullable<typeof venue.value>,
  id: string,
): Readonly<Record<string, unknown>> | undefined {
  return (
    v.units.find((u) => u.id === id)?.attrs ??
    v.pois.find((p) => p.id === id)?.attrs ??
    v.network.nodes.find((n) => n.id === id)?.attrs
  )
}

/** Описание: живой слой → статика attrs.description (строка или {et,en,ru}). */
function descriptionOf(v: NonNullable<typeof venue.value>, id: string): string | null {
  const live = liveOf(id)
  if (live?.description) {
    const text = localized(live.description, getLang(), v.venue.default_language)
    if (text) return text
  }
  const raw = staticAttrsOf(v, id)?.['description']
  if (typeof raw === 'string') return raw || null
  if (typeof raw === 'object' && raw !== null) {
    return localized(raw as Record<string, string>, getLang(), v.venue.default_language) || null
  }
  return null
}

/** Фото цели: карточка арендатора (живой слой) → attrs.photos из редактора. */
function photosOf(v: NonNullable<typeof venue.value>, id: string): readonly string[] {
  const live = liveOf(id)
  if (live?.photos && live.photos.length > 0) return live.photos
  const raw = staticAttrsOf(v, id)?.['photos']
  return Array.isArray(raw) ? raw.filter((u): u is string => typeof u === 'string') : []
}

/** Полное время до цели: внутренний план + уличное плечо (№23). */
function totalDurationS(planDurationS: number): number {
  return planDurationS + (externalLeg.value?.durationS ?? 0)
}

const TRAVEL_MODES = [
  ['foot', 'travel.foot'],
  ['vehicle', 'travel.vehicle'],
  ['truck', 'travel.truck'],
] as const

/**
 * Переключатель способа передвижения. Показывается, только когда транспорт
 * уместен (запрос владельца 03.08.2026): внутри здания «На авто/Фура» до
 * полки — бессмыслица, а из дома или через территорию промзоны — нужный
 * выбор. Критерий — travel-choice.ts: пеший маршрут задевает дороги либо
 * гео-старт вне площадки. Эталон — ПЕШИЙ маршрут, не выбранный режим:
 * иначе чипы мигали бы при переключении. Когда выбор скрыт, режим мягко
 * сбрасывается на «пешком» — застрявшая «фура» дала бы странный маршрут.
 */
function TravelModeChips({ v, id }: { v: NonNullable<typeof venue.value>; id: string }) {
  const g = graph.value
  const geo = geoStart.value
  const anchor = anchorId.value
  const mode = travelMode.value
  const plan = routePlan.value
  const footNodeIds = useMemo(() => {
    if (!g) return null
    if (mode === 'foot' && plan && plan.targetId === id) return plan.nodeIds
    const start = resolveStart(v, { anchorId: anchor ?? undefined, geo: geo ?? undefined })
    return start ? (planRoute(v, g, start, id, 'foot')?.nodeIds ?? null) : null
  }, [v, g, id, anchor, geo, mode, plan])
  const show = g !== null && showTravelChoice(v, g, footNodeIds, geo)
  useEffect(() => {
    if (!show && mode !== 'foot') setTravelMode('foot')
  }, [show, mode])
  if (!show) return null
  return (
    <fieldset class="mode-chips" aria-label={tr('travel.label')}>
      {TRAVEL_MODES.map(([m, key]) => (
        <button
          type="button"
          key={m}
          class={`mode-chip mode-chip-icononly${mode === m ? ' mode-chip-on' : ''}`}
          aria-pressed={mode === m}
          aria-label={tr(key)}
          title={tr(key)}
          onClick={() => setTravelMode(m)}
        >
          <span
            class="mode-chip-icon"
            aria-hidden="true"
            dangerouslySetInnerHTML={{ __html: TRAVEL_ICONS[m] }}
          />
        </button>
      ))}
    </fieldset>
  )
}

/**
 * Мини-статы маршрута (ScreenTarget прототипа): время · путь · этажи —
 * три карточки. Время окрашено цветом пути: оно и есть продукт.
 */
function TargetStats({ v }: { v: NonNullable<typeof venue.value> }) {
  const plan = routePlan.value
  if (!plan) return null
  const levels = planLevelsLabel(v, plan)
  return (
    <div class="tc-stats">
      <div class="tc-stat">
        <div class="tc-stat-label">{tr('target.stat_time')}</div>
        <div class="tc-stat-value tc-stat-time tabular">
          {formatDuration(totalDurationS(plan.durationS))}
        </div>
      </div>
      <div class="tc-stat">
        <div class="tc-stat-label">{tr('target.stat_way')}</div>
        <div class="tc-stat-value tabular">{formatDistance(plan.distanceM)}</div>
      </div>
      {levels && (
        <div class="tc-stat">
          <div class="tc-stat-label">{tr('target.stat_levels')}</div>
          <div class="tc-stat-value">{levels}</div>
        </div>
      )}
      {/* Вход в здание не там, где стоишь, — честная пометка о начале пути. */}
      {plan.start.source === 'entrance' && !levels && (
        <div class="tc-stat">
          <div class="tc-stat-label">{tr('target.stat_start')}</div>
          <div class="tc-stat-value tc-stat-small">{tr('target.from_entrance')}</div>
        </div>
      )}
    </div>
  )
}

/** Главная кнопка состояния ЦЕЛЬ: слева «Маршрут», справа время — оно и есть
 *  продукт, поэтому живёт ВНУТРИ кнопки и «дышит» весом шрифта. */
function RouteBlock({ id }: { id: string }) {
  const plan = routePlan.value
  if (!plan) return <p class="sheet-eta sheet-warn">{tr('target.no_route')}</p>
  return (
    <div class="tc-cta-row">
      <button
        type="button"
        class="cta-hero light-sweep"
        disabled={!venueMap.value}
        onClick={showRoute}
      >
        <span class="cta-hero-label">{tr('target.route')}</span>
        <span class="cta-hero-value tabular weight-breathe">
          {formatDuration(totalDurationS(plan.durationS))}
        </span>
      </button>
      <button
        type="button"
        class="tc-round tc-round-glass"
        aria-label={tr('qrpass.kicker')}
        title={tr('qrpass.kicker')}
        onClick={() => {
          qrPassTargetId.value = id
        }}
      >
        <span class="glyph" aria-hidden="true" dangerouslySetInnerHTML={{ __html: GLYPH_QR }} />
      </button>
    </div>
  )
}

/** Ритейл-блок полки: код места, «обычно здесь» и кнопка «в список». */
function ShelfBlock({ v, id }: { v: NonNullable<typeof venue.value>; id: string }) {
  const products = productsOnShelf(v, id)
  const inList = shoppingKeys.value.includes(id)
  const showListButton = (v.products ?? []).length > 0
  if (products.length === 0 && !showListButton) return null
  const lang = getLang()
  const fb = v.venue.default_language
  const shown = products.slice(0, 6)
  return (
    <>
      {products.length > 0 && (
        <div class="shelf-products">
          <p class="tech-label">{tr('target.usually_here')}</p>
          <div class="shelf-products-chips">
            {shown.map((p) => (
              <span key={p.id} class="info-chip">
                {localized(p.name, lang, fb)}
              </span>
            ))}
            {products.length > shown.length && (
              <span class="info-chip">+{products.length - shown.length}</span>
            )}
          </div>
        </div>
      )}
      {showListButton && (
        <button
          type="button"
          class="btn-secondary"
          onClick={() => (inList ? removeFromList(v.venue.id, id) : addToList(v.venue.id, id))}
        >
          {inList ? `✓ ${tr('target.in_list')}` : `+ ${tr('target.add_to_list')}`}
        </button>
      )}
    </>
  )
}

/** Раскрываемая часть карточки: фото и подробности. «Подробнее» без контента —
 *  обман, поэтому кнопка появляется только когда есть что раскрыть. */
function CardDetails({ v, id }: { v: NonNullable<typeof venue.value>; id: string }) {
  // Подробная карточка — настройка владельца (venue_settings.card_full, дефолт ВКЛ).
  if (v.venue.card_full === false) return null
  const photos = photosOf(v, id)
  if (photos.length === 0 && !hasCardDetails(v, id)) return null
  return (
    <>
      {cardDetailsOpen.value && (
        <>
          <PhotoStrip photos={photos} />
          {v.units.some((u) => u.id === id) ? (
            <BuildingDetails id={id} />
          ) : (
            <TenantDetails id={id} />
          )}
        </>
      )}
      <button type="button" class="card-details-toggle" onClick={toggleCardDetails}>
        {cardDetailsOpen.value ? `${tr('card.less')} ▴` : `${tr('card.more')} ↓`}
      </button>
    </>
  )
}

/** Контакты и доступность: сайт и чип «без лестниц» (часы — пилюлей в шапке). */
function CardFacts({ website }: { website: string | null }) {
  const stepFree = stepFreeOk.value === true
  if (!website && !stepFree) return null
  return (
    <>
      {/* Сайт арендатор заполняет в кабинете — ссылка там, где ищут контакты. */}
      {website && (
        <a class="sheet-phone" href={website} target="_blank" rel="noopener noreferrer">
          {website.replace(/^https?:\/\//, '')}
        </a>
      )}
      {/* Чип о МАРШРУТЕ, а не о помещении: «Можно без лестниц» без значка и
          без пояснения читалось как непонятная пометка на магазине
          (замечание владельца 31.07). Теперь — коляска и прямой текст. */}
      {stepFree && (
        <div class="info-chips">
          <span class="info-chip info-chip-access" title={tr('target.step_free_hint')}>
            <span
              class="glyph"
              aria-hidden="true"
              dangerouslySetInnerHTML={{ __html: GLYPH_WHEELCHAIR }}
            />
            {tr('target.step_free')}
          </span>
        </div>
      )}
    </>
  )
}

export function TargetCard() {
  langTick.value
  const v = venue.value
  const id = targetId.value
  if (!v || !id) return null
  const secondaryName = secondaryNameOf(v, id)
  const description = descriptionOf(v, id)
  const phone = phoneOf(id)
  const hours = hoursOf(v, id)
  // Код места («R155») — тот же, что на ценнике и табличке ряда: продавец
  // диктует его голосом, покупатель сверяет глазами.
  const poi = v.pois.find((p) => p.id === id)
  const shelfCode = poi ? shelfCodeOf(poi) : null

  return (
    <section class="sheet">
      <div class="sheet-head">
        <div class="tc-headtext">
          {/* Пилюли «что это и где» — до имени: взгляд сразу отвечает себе
              «кафе, второй этаж, открыто», а потом читает вывеску. */}
          <div class="tc-pills">
            <span class="pill pill-amber">
              {categoryLabel(featureCategory(v, id))}
              {floorOfTarget(v, id)}
            </span>
            {hours && <span class="pill pill-path">{hours}</span>}
          </div>
          <h2 class="sheet-title font-display">{featureName(v, id)}</h2>
          {(secondaryName || shelfCode) && (
            <p class="sheet-sub">
              {shelfCode ? tr('target.room_n', { code: shelfCode }) : ''}
              {shelfCode && secondaryName ? ' · ' : ''}
              {secondaryName ?? ''}
            </p>
          )}
        </div>
        {/* Позвонить — круглая path-кнопка: единственное действие, которое
            уводит из карты, поэтому оно отдельное и заметное. */}
        {phone && (
          <a class="tc-round tc-round-path" href={`tel:${phone}`} title={phone}>
            <span
              class="glyph"
              aria-hidden="true"
              dangerouslySetInnerHTML={{ __html: GLYPH_PHONE }}
            />
            <span class="sr-only">{phone}</span>
          </a>
        )}
        <button
          type="button"
          class="icon-btn"
          aria-label={tr('common.close')}
          onClick={closeTarget}
        >
          ✕
        </button>
      </div>
      {/* Значки двери: главный вход и доступность — до описания, потому что
          отвечают на вопрос «мне сюда идти?» раньше любого текста. */}
      <DoorBadges facts={doorFactsOf(staticAttrsOf(v, id))} />
      {description && <p class="sheet-sub">{description}</p>}
      {/* Время · путь · этажи — три карточки: ответ «сколько идти» до кнопки. */}
      <TargetStats v={v} />
      <CardFacts website={liveWebsite(id)} />
      <ShelfBlock v={v} id={id} />
      <TravelModeChips v={v} id={id} />
      {/* Как подниматься и «без лестниц» — до кнопки «Маршрут», а не после. */}
      <ClimbPicker />
      <ClimbNoAltHint />
      <RouteBlock id={id} />
      <CardDetails v={v} id={id} />
      {isParking(v, featureCategory(v, id)) && !parkedCar.value && (
        <button type="button" class="btn-secondary btn-glyph" onClick={() => rememberCar(id)}>
          <span
            class="glyph"
            aria-hidden="true"
            dangerouslySetInnerHTML={{ __html: GLYPH_PARKING }}
          />
          {tr('car.remember')}
        </button>
      )}
      {/* Подробная карточка — настройка владельца (venue_settings.card_full). */}
      {v.venue.card_full !== false && <EditInCabinetLink />}
    </section>
  )
}
